import { Button } from '@cloudflare/kumo';
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Glyph, ICONS } from './icons';

/**
 * Página de criação/edição, no estilo do painel da Cloudflare: o conteúdo principal troca pelo
 * formulário. Faixa de cabeçalho branca com borda (voltar, título, descrição), coluna central de
 * ~800px sem cartão em volta dos campos, seções separadas por linhas e rodapé com texto de apoio
 * à esquerda e Cancelar/Salvar à direita. Modais ficam para confirmações e ações rápidas.
 */

export interface FormPageProps {
  /** Para onde o link "voltar" e o Cancelar levam. */
  backTo: string;
  /** Faz o "voltar" ser um botão (troca de conteúdo sem mudar de rota). */
  onBack?: () => void;
  backLabel: string;
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  /** Texto do botão principal (ex.: "Criar contato"). */
  submitLabel: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  /** Se o formulário for um <form>, passe o id dele para o botão do rodapé enviá-lo. */
  formId?: string;
  onSubmit?: () => void;
  onCancel?: () => void;
  /** Texto de apoio à esquerda do rodapé. */
  footerNote?: ReactNode;
}

const backClass = 'inline-flex items-center gap-1 text-sm text-kumo-link underline underline-offset-2 hover:opacity-80';

export function FormPage({ backTo, onBack, backLabel, title, description, children, submitLabel, submitting, submitDisabled, formId, onSubmit, onCancel, footerNote }: FormPageProps) {
  return (
    <div className="min-h-full bg-kumo-base">
      <header className="border-b border-kumo-line bg-kumo-base px-4 pb-6 pt-6 md:px-8">
        {onBack ? (
          <button type="button" onClick={onBack} className={backClass}>
            <Glyph icon={ICONS.ArrowLeft} size={14} />
            {backLabel}
          </button>
        ) : (
          <Link to={backTo} className={backClass}>
            <Glyph icon={ICONS.ArrowLeft} size={14} />
            {backLabel}
          </Link>
        )}
        <h1 className="mt-4 text-xl font-semibold text-kumo-default">{title}</h1>
        {description ? <p className="mt-2 max-w-3xl text-kumo-subtle">{description}</p> : null}
      </header>
      <div className="mx-auto max-w-[800px] px-4 pb-12 pt-10 md:px-0">
        <div className="flex flex-col">{children}</div>
        <div className="mt-10 flex flex-wrap items-start justify-between gap-6 border-t border-kumo-line pt-8">
          <div className="max-w-xl text-sm text-kumo-default">{footerNote}</div>
          <div className="ml-auto flex gap-2">
            {onCancel ? (
              <Button variant="secondary" type="button" onClick={onCancel}>Cancelar</Button>
            ) : (
              <Link to={backTo}><Button variant="secondary" type="button">Cancelar</Button></Link>
            )}
            <Button variant="primary" type={formId ? 'submit' : 'button'} form={formId} onClick={formId ? undefined : onSubmit} loading={submitting} disabled={submitDisabled || submitting}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Seção da página de formulário: sem cartão, separada da anterior por uma linha (como na Cloudflare). */
export function FormSection({ title, description, children }: { title?: ReactNode; description?: ReactNode; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-4 border-b border-kumo-line py-6 first:pt-0 last:border-b-0">
      {title ? (
        <div>
          <h2 className="text-lg font-semibold text-kumo-default">{title}</h2>
          {description ? <p className="mt-1 text-sm text-kumo-subtle">{description}</p> : null}
        </div>
      ) : null}
      {children}
    </section>
  );
}

export interface RadioCardOption { value: string; title: ReactNode; description?: ReactNode }

/** Escolha entre poucas opções em cartões selecionáveis (como a localização do bucket na Cloudflare). */
export function RadioCards({ label, value, options, onChange, columns = 1 }: { label: string; value: string; options: RadioCardOption[]; onChange: (value: string) => void; columns?: 1 | 2 | 3 }) {
  const grid = columns === 3 ? 'sm:grid-cols-3' : columns === 2 ? 'sm:grid-cols-2' : '';
  return (
    <div role="radiogroup" aria-label={label} className={`grid gap-3 ${grid}`}>
      {options.map((o) => {
        const checked = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(o.value)}
            className={`flex items-start gap-3 rounded-lg border p-4 text-left transition-colors ${checked ? 'border-kumo-brand bg-kumo-tint' : 'border-kumo-line bg-kumo-base hover:bg-kumo-tint'}`}
          >
            <span className={`mt-0.5 flex size-4 shrink-0 items-center justify-center rounded-full border ${checked ? 'border-kumo-brand' : 'border-kumo-line'}`}>
              {checked ? <span className="size-2 rounded-full bg-kumo-brand" /> : null}
            </span>
            <span className="flex flex-col gap-0.5">
              <span className="text-sm font-medium text-kumo-default">{o.title}</span>
              {o.description ? <span className="text-sm text-kumo-subtle">{o.description}</span> : null}
            </span>
          </button>
        );
      })}
    </div>
  );
}
